import type { SvgIconComponent } from "@mui/icons-material";
import LanguageIcon from "@mui/icons-material/LanguageRounded";
import PublishDialogIcon from "@entrilia/shared/icons/PublishDialogIcon";
import PublishToPreviewDialogIcon from "@entrilia/shared/icons/PublishToPreviewDialogIcon";
import type { InvestorCommunicationStatus } from "@entrilia/shared/api/investorCommunicationsTypes";
import UnpublishIcon from "@entrilia/shared/icons/UnpublishIcon";
import VisibleIcon from "@entrilia/shared/icons/VisibleIcon";

export interface InvestorCommunicationStatusTransition {
  to: InvestorCommunicationStatus;
  label: string;
  dialogActionLabel: string;
  detailLines: string[];
  showNewsNotLiveWarning?: boolean;
  Icon: SvgIconComponent;
  DialogIcon: SvgIconComponent;
}

const publishToPreviewTransition: InvestorCommunicationStatusTransition = {
  to: "Preview",
  label: "Publish to Preview",
  dialogActionLabel: "Publish",
  detailLines: [
    "You are about to publish this communication in Preview mode.",
    "It won't be available to investors yet, but you can inspect the portal as an investor contact.",
  ],
  Icon: VisibleIcon,
  DialogIcon: PublishToPreviewDialogIcon,
};

const publishToLiveTransition: InvestorCommunicationStatusTransition = {
  to: "Live",
  label: "Publish to Live",
  dialogActionLabel: "Publish",
  detailLines: ["You are about to make this communication Live and available for investors."],
  Icon: LanguageIcon,
  DialogIcon: PublishDialogIcon,
};

const publishToLiveWhenNewsHiddenTransition: InvestorCommunicationStatusTransition = {
  ...publishToLiveTransition,
  detailLines: ["You are about to set this communication status to 'Live'."],
  showNewsNotLiveWarning: true,
};

const unpublishToPreviewTransition: InvestorCommunicationStatusTransition = {
  to: "Preview",
  label: "Unpublish to Preview",
  dialogActionLabel: "Unpublish",
  detailLines: ["The content will be returned to Preview and will no longer be available to investors."],
  Icon: UnpublishIcon,
  DialogIcon: PublishToPreviewDialogIcon,
};

export const getPublishTransition = (
  status: InvestorCommunicationStatus,
  isNewsVisibleToInvestors: boolean
): InvestorCommunicationStatusTransition | undefined => {
  if (status === "Draft") {
    return publishToPreviewTransition;
  }

  if (status === "Preview") {
    return isNewsVisibleToInvestors ? publishToLiveTransition : publishToLiveWhenNewsHiddenTransition;
  }

  return undefined;
};

export const getUnpublishTransition = (
  status: InvestorCommunicationStatus
): InvestorCommunicationStatusTransition | undefined => (status === "Live" ? unpublishToPreviewTransition : undefined);
